import { classes } from 'html-classes';
import { ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';

import styles from './CollapsibleList.module.scss';

type Props = {
  itemsToShow: ReactNode[];
  itemsToHide: ReactNode[];
  showMoreText?: string;
  showLessText?: string;
  buttonContainerClassName?: string;
  buttonClassName?: string;
  useDefaultButtonStyles?: boolean;
  sublistClassName?: string;
  sublistClassNameCollapsible?: string;
  onToggle?: (isOpen: boolean) => void;
};

export const CollapsibleList = ({
  itemsToShow,
  itemsToHide,
  showMoreText,
  showLessText,
  buttonContainerClassName,
  buttonClassName,
  useDefaultButtonStyles = true,
  sublistClassName,
  sublistClassNameCollapsible,
  onToggle,
}: Props) => {
  const { t } = useTranslation();

  const [isListOpen, setIsListOpen] = useState(false);
  const collapsibleRef = useRef<HTMLDivElement>(null);
  const baseRef = useRef<HTMLSpanElement>(null);

  const expandButtonHandler = () => {
    const nextState = !isListOpen;
    setIsListOpen(nextState);
    if (isListOpen && baseRef?.current) {
      const position =
        window.scrollY + baseRef.current.getBoundingClientRect().top - 220;
      if (position) {
        window.scrollTo({
          top: position,
          left: 0,
          behavior: 'smooth',
        });
      }
    }
    onToggle?.(nextState);
  };

  return (
    <>
      <i className={styles.baseAnchor} ref={baseRef} />
      {itemsToShow}
      {itemsToHide.length > 0 && (
        <div
          className={classes(['__collapsible', sublistClassNameCollapsible])}
          style={{
            height:
              !isListOpen || !collapsibleRef
                ? '0px'
                : `${collapsibleRef.current?.offsetHeight}px`,
          }}
        >
          <div className={sublistClassName} ref={collapsibleRef}>
            {itemsToHide}
          </div>
        </div>
      )}
      {itemsToHide.length > 0 && (
        <div
          className={classes([
            useDefaultButtonStyles && styles.buttonContainer,
            buttonContainerClassName,
          ])}
          data-company={company.name}
        >
          <button
            className={classes([
              useDefaultButtonStyles && 'button _bordered',
              useDefaultButtonStyles && styles.button,
              buttonClassName,
            ])}
            onClick={expandButtonHandler}
          >
            {isListOpen
              ? (showLessText ?? t('showLess'))
              : (showMoreText ?? t('showMore'))}{' '}
          </button>
        </div>
      )}
    </>
  );
};
